import { defineStore } from "pinia";

import OrderCalculationsApi from "@/services/api/orderCalculationsApi";
import { getCompanyId } from "@/utils/userUtils";

// Хранит реальные данные блока ПЛАН/ФАКТ открытого заказа.
// Оба блока приходят готовыми с backend, frontend не пересчитывает финансовые показатели.
export const useOrderCalculationStore = defineStore("orderCalculation", {
  state: () => ({
    calculations: [],
    currentPlan: null,
    planPagination: null,
    fact: null,
    companyId: getCompanyId(),
    isLoading: false,
    isLoadingPlanHistory: false,
    isCalculating: false,
    isFactLoading: false,
    isFactSubmitting: false,
    planOrderId: null,
    factOrderId: null,
    planRequestRevision: 0,
    calculationRequestRevision: 0,
    factRequestRevision: 0,
  }),

  getters: {
    latestAttempt: (state) => state.calculations[0] || null,
    hasMorePlanHistory: (state) => Boolean(state.planPagination?.next),
    planHistoryCount: (state) =>
      state.planPagination?.count ?? state.calculations.length,
    planSummaryItems() {
      const breakdown = this.currentPlan?.breakdown || [];
      const pinned = breakdown
        .filter((item) => item.show_in_summary && !item.semantic_key)
        .slice(0, 2);
      return breakdown.filter((item) =>
        Boolean(item.semantic_key) || pinned.includes(item)
      );
    },
    factTotal: (state) => Number(state.fact?.expenses_total || 0),
    factProfit: (state) => state.fact?.profit ?? null,
    factOrderPrice: (state) => state.fact?.order_price ?? null,
    factTelemetryMileageKm: (state) => state.fact?.telemetry_mileage_km ?? null,
    factMileageKm: (state) => state.fact?.actual_mileage_km ?? null,
    factMileageOverridden: (state) => Boolean(state.fact?.mileage_overridden),
    factMileageUpdatedBy: (state) => state.fact?.mileage_updated_by ?? null,
    factMileageUpdatedAt: (state) => state.fact?.mileage_updated_at ?? null,
    factTelemetryCalculatedAt: (state) => state.fact?.telemetry_calculated_at ?? null,
    factMileageRefreshPending: (state) => Boolean(state.fact?.mileage_refresh_pending),
    factProfitPerKm: (state) => state.fact?.profit_per_km ?? null,
    factExpenseCount: (state) => state.fact?.expense_count || 0,
    expenseCategories: (state) => state.fact?.categories || [],
    latestExpenses: (state) => state.fact?.latest_expenses || [],
  },

  actions: {
    responseResult(response) {
      return response.data.result || response.data || [];
    },

    async fetchPlan(orderId) {
      const orderKey = String(orderId);
      const requestRevision = ++this.planRequestRevision;
      this.planOrderId = orderKey;
      this.calculations = [];
      this.currentPlan = null;
      this.planPagination = null;
      this.isLoadingPlanHistory = false;
      this.isLoading = true;
      try {
        const response = await OrderCalculationsApi.index(
          this.companyId,
          orderId,
          1
        );
        // Страница заказа переиспользует store: поздний ответ предыдущего
        // заказа не должен подменить данные уже открытого заказа.
        if (
          requestRevision !== this.planRequestRevision
          || orderKey !== this.planOrderId
        ) {
          return [];
        }

        const calculations = this.responseResult(response);
        this.calculations = calculations;
        this.currentPlan = response.data.current_plan || null;
        this.planPagination = response.data.pagination_meta || null;
        return calculations;
      } finally {
        if (requestRevision === this.planRequestRevision) {
          this.isLoading = false;
        }
      }
    },

    // История растёт со временем, поэтому старые версии догружаются только по запросу.
    async loadMorePlanHistory(orderId) {
      const orderKey = String(orderId);
      const nextPage = this.planPagination?.next;
      if (
        !nextPage
        || this.isLoadingPlanHistory
        || orderKey !== this.planOrderId
      ) {
        return [];
      }

      const requestRevision = this.planRequestRevision;
      this.isLoadingPlanHistory = true;
      try {
        const response = await OrderCalculationsApi.index(
          this.companyId,
          orderId,
          nextPage
        );
        if (
          requestRevision !== this.planRequestRevision
          || orderKey !== this.planOrderId
        ) {
          return [];
        }

        const calculations = this.responseResult(response);
        const existingIds = new Set(this.calculations.map((item) => item.id));
        this.calculations = [
          ...this.calculations,
          ...calculations.filter((item) => !existingIds.has(item.id)),
        ];
        this.planPagination = response.data.pagination_meta || null;
        return calculations;
      } finally {
        if (requestRevision === this.planRequestRevision) {
          this.isLoadingPlanHistory = false;
        }
      }
    },

    async recalculate(orderId, calculatorPresetId, presetParams = []) {
      const orderKey = String(orderId);
      const requestRevision = ++this.calculationRequestRevision;
      this.isCalculating = true;
      try {
        const response = await OrderCalculationsApi.create(
          this.companyId,
          orderId,
          calculatorPresetId,
          presetParams
        );
        const calculation = this.responseResult(response);
        // Backend завершает каждый запрос, но UI принимает только последний
        // пересчёт всё ещё открытого заказа.
        if (
          requestRevision !== this.calculationRequestRevision
          || (this.planOrderId !== null && this.planOrderId !== orderKey)
        ) {
          return null;
        }

        await this.fetchPlan(orderId);
        if (
          requestRevision !== this.calculationRequestRevision
          || this.planOrderId !== orderKey
        ) return null;

        return calculation;
      } finally {
        if (requestRevision === this.calculationRequestRevision) {
          this.isCalculating = false;
        }
      }
    },

    // Backend возвращает готовый ФАКТ; store только защищает экран от поздних ответов.
    async fetchFact(orderId, { silent = false } = {}) {
      if (!silent) this.fact = null;
      if (!orderId) return null;

      const orderKey = String(orderId);
      const requestRevision = ++this.factRequestRevision;
      this.factOrderId = orderKey;
      if (!silent) this.isFactLoading = true;
      try {
        const response = await OrderCalculationsApi.fact(this.companyId, orderId);
        if (
          requestRevision !== this.factRequestRevision
          || orderKey !== this.factOrderId
        ) {
          return null;
        }

        const fact = this.responseResult(response);
        this.fact = fact;
        return fact;
      } finally {
        if (requestRevision === this.factRequestRevision) {
          if (!silent) this.isFactLoading = false;
        }
      }
    },

    async updateFactMileage(orderId, actualMileageKm) {
      this.isFactSubmitting = true;
      try {
        const response = await OrderCalculationsApi.updateFactMileage(
          this.companyId,
          orderId,
          actualMileageKm
        );
        const fact = this.responseResult(response);
        if (this.factOrderId === null || this.factOrderId === String(orderId)) {
          this.fact = fact;
          this.factOrderId = String(orderId);
        }
        return fact;
      } finally {
        this.isFactSubmitting = false;
      }
    },

    async refreshFactMileage(orderId) {
      this.isFactSubmitting = true;
      try {
        const response = await OrderCalculationsApi.refreshFactMileage(
          this.companyId,
          orderId
        );
        const refreshResult = this.responseResult(response);
        const fact = refreshResult.fact;
        if (this.factOrderId === null || this.factOrderId === String(orderId)) {
          this.fact = fact;
          this.factOrderId = String(orderId);
        }
        return refreshResult;
      } finally {
        this.isFactSubmitting = false;
      }
    },
  },
});
